import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';

interface User {
  email: string;
  name: string;
}

interface AuthSession {
  user: User;
  token: string;
  reviewToken?: string;
  expiresAt: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const AUTH_STORAGE_KEY = 'claro-auth-session';
export const AUTH_INVALID_EVENT = 'claro:auth-invalid';
const SESSION_DURATION_MS = 8 * 60 * 60 * 1000; // 8 horas

function validateSession(session: AuthSession | null): boolean {
  if (!session) return false;
  return Boolean(session.token) && Date.now() <= session.expiresAt;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    // Verificar se há sessão salva e válida
    const saved = localStorage.getItem(AUTH_STORAGE_KEY);
    if (saved) {
      try {
        const session: AuthSession = JSON.parse(saved);
        if (validateSession(session)) {
          return session.user;
        }
        // Sessão inválida, limpar
        localStorage.removeItem(AUTH_STORAGE_KEY);
      } catch {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      }
    }
    return null;
  });

  // Verificar sessão periodicamente
  useEffect(() => {
    const interval = setInterval(() => {
      const saved = localStorage.getItem(AUTH_STORAGE_KEY);
      if (saved) {
        try {
          const session: AuthSession = JSON.parse(saved);
          if (!validateSession(session)) {
            setUser(null);
            localStorage.removeItem(AUTH_STORAGE_KEY);
          }
        } catch {
          setUser(null);
          localStorage.removeItem(AUTH_STORAGE_KEY);
        }
      }
    }, 60000); // Verificar a cada minuto

    return () => clearInterval(interval);
  }, []);

  // APIs protegidas podem invalidar a sessão antes do prazo salvo no navegador
  // (por exemplo, após deploy ou reinício do servidor). Mantém a UI sincronizada
  // com a autenticação real para que o ProtectedRoute peça um novo login.
  useEffect(() => {
    const invalidate = () => {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      setUser(null);
    };
    window.addEventListener(AUTH_INVALID_EVENT, invalidate);
    return () => window.removeEventListener(AUTH_INVALID_EVENT, invalidate);
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    try {
      // Autenticação via API do servidor
      const response = await fetch('/claro/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      
      if (data.success && data.user && (data.token || data.reviewToken)) {
        const now = Date.now();
        const expiresAt = now + SESSION_DURATION_MS;
        
        const session: AuthSession = {
          user: { email: data.user.email, name: data.user.name },
          token: data.token || data.reviewToken,
          reviewToken: data.reviewToken,
          expiresAt,
        };
        
        setUser(session.user);
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    try {
      const session = JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) || '{}');
      if (session.reviewToken) void fetch('/claro/api/auth/logout', { method: 'POST', headers: { Authorization: `Bearer ${session.reviewToken}` } });
    } catch { /* Local logout must still complete. */ }
    setUser(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
